// components/admin/ViewsTab.js - CORRECTED VERSION
import React, { useState, useEffect } from "react"
import {
  fetchProductByCode,
  fetchCategory,
  addProductView,
  updateProductView,
  deleteProductView,
} from "../../api"

const ViewsTab = ({
  selectedProductCode,
  selectedProduct,
  setLoading,
  isOverlay = false,
  isMinimized: externalIsMinimized = false,
}) => {
  // State for views
  const [productViews, setProductViews] = useState([])
  const [familyViews, setFamilyViews] = useState([])
  const [productData, setProductData] = useState(null)
  const [familyData, setFamilyData] = useState(null)

  // UI state
  const [showViewForm, setShowViewForm] = useState(false)
  const [editingView, setEditingView] = useState(null)

  // Form state
  const [formData, setFormData] = useState({
    label: "",
    cameraPositionX: 0,
    cameraPositionY: 0,
    cameraPositionZ: 0,
    cameraTargetX: 0,
    cameraTargetY: 0,
    cameraTargetZ: 0,
    cameraFOV: 45,
    animate: false,
    animationDuration: 1.5,
    enabledForUsers: false,
    saveToFamily: false,
  })

  // Current view data from the 3D viewer
  const [currentViewData, setCurrentViewData] = useState(null)

  // Load views when product changes
  useEffect(() => {
    if (selectedProductCode && selectedProduct) {
      loadProductAndFamilyViews()
    }
  }, [selectedProductCode, selectedProduct])

  // Load both product and family views
  const loadProductAndFamilyViews = async () => {
    if (!selectedProductCode || !selectedProduct) {
      console.log("No product selected, skipping views load")
      return
    }

    setLoading(true)
    try {
      // Get fresh product data
      const product = await fetchProductByCode(selectedProductCode)
      setProductData(product)

      // Extract product custom views with safety check
      const prodViews = Array.isArray(product.customViews)
        ? product.customViews
        : []
      setProductViews(prodViews)

      // Get category and family info - try multiple sources
      let categoryName =
        product.category || product._category || selectedProduct?.category
      let familyName =
        product.family || product._family || selectedProduct?.family

      console.log(`Product data:`, {
        code: product.code,
        category: categoryName,
        family: familyName,
        selectedProductCategory: selectedProduct?.category,
        selectedProductFamily: selectedProduct?.family,
      })

      if (categoryName && familyName) {
        try {
          const categoryData = await fetchCategory(categoryName)
          const family = categoryData?.families?.[familyName]

          if (family) {
            setFamilyData(family)
            // Extract family custom views with safety check
            const famViews = Array.isArray(family.customViews)
              ? family.customViews
              : []
            setFamilyViews(famViews)
            console.log(
              `Loaded ${prodViews.length} product views and ${famViews.length} family views`
            )
          } else {
            console.warn(
              `Family ${familyName} not found in category ${categoryName}`
            )
            setFamilyData(null)
            setFamilyViews([])
          }
        } catch (error) {
          console.error("Failed to load family data:", error)
          setFamilyData(null)
          setFamilyViews([])
        }
      } else {
        console.warn("Product missing category or family information", {
          productCategory: categoryName,
          productFamily: familyName,
          selectedProductCategory: selectedProduct?.category,
          selectedProductFamily: selectedProduct?.family,
        })
        setFamilyData(null)
        setFamilyViews([])
      }
    } catch (error) {
      console.error("Failed to load views:", error)
      alert(`Errore nel caricamento delle views: ${error.message}`)
      setProductViews([])
      setFamilyViews([])
    } finally {
      setLoading(false)
    }
  }

  // CORRECTED: Save view using the proper API structure
  const saveViewToProduct = async (viewData, saveToFamily = false) => {
    try {
      console.log("Saving view with data:", {
        selectedProductCode,
        viewData,
        saveToFamily,
      })

      // CORRECTED: Call API with proper parameters
      await addProductView(selectedProductCode, viewData, saveToFamily)
      console.log(
        `Saved custom view to ${
          saveToFamily ? "family" : "product"
        } ${selectedProductCode}`
      )

      // Reload views to get updated data
      await loadProductAndFamilyViews()
    } catch (error) {
      console.error("Failed to save view:", error)
      alert(`Errore nel salvataggio della view: ${error.message}`)
      throw error
    }
  }

  // CORRECTED: Update view using the proper API structure
  const updateViewInProduct = async (
    oldLabel,
    viewData,
    isFromFamily = false
  ) => {
    try {
      // CORRECTED: Call API with proper parameters
      await updateProductView(
        selectedProductCode,
        oldLabel,
        viewData,
        isFromFamily
      )
      console.log(
        `Updated custom view in ${
          isFromFamily ? "family" : "product"
        } ${selectedProductCode}`
      )

      // Reload views to get updated data
      await loadProductAndFamilyViews()
    } catch (error) {
      console.error("Failed to update view:", error)
      alert(`Errore nell'aggiornamento della view: ${error.message}`)
      throw error
    }
  }

  // CORRECTED: Delete view using the proper API structure
  const deleteViewFromProduct = async (viewLabel, isFromFamily = false) => {
    try {
      // CORRECTED: Call API with proper parameters
      await deleteProductView(selectedProductCode, viewLabel, isFromFamily)
      console.log(
        `Deleted custom view from ${
          isFromFamily ? "family" : "product"
        } ${selectedProductCode}`
      )

      // Reload views to get updated data
      await loadProductAndFamilyViews()
    } catch (error) {
      console.error("Failed to delete view:", error)
      alert(`Errore nell'eliminazione della view: ${error.message}`)
      throw error
    }
  }

  // Handle getting current camera position from the viewer
  const getCurrentCameraPosition = () => {
    if (!window.emViewers || !window.emViewers["emersyaIframe"]) {
      alert("Visualizzatore 3D non disponibile")
      return
    }

    const viewer = window.emViewers["emersyaIframe"]

    try {
      viewer.getCamera().then((cameraData) => {
        console.log("Camera data received:", cameraData)
        setCurrentViewData(cameraData)

        // Validate camera data before using
        if (!cameraData || !cameraData.position || !cameraData.target) {
          console.error("Invalid camera data structure:", cameraData)
          alert("Dati camera non validi")
          return
        }

        // CORRECTED: Safe handling of Float32Array and conversion
        const posX = cameraData.position[0] || cameraData.position.x || 0
        const posY = cameraData.position[1] || cameraData.position.y || 0
        const posZ = cameraData.position[2] || cameraData.position.z || 0

        const targetX = cameraData.target[0] || cameraData.target.x || 0
        const targetY = cameraData.target[1] || cameraData.target.y || 0
        const targetZ = cameraData.target[2] || cameraData.target.z || 0

        // Update form with camera data - with safety checks
        setFormData((prev) => ({
          ...prev,
          cameraPositionX: parseFloat(posX.toFixed(3)),
          cameraPositionY: parseFloat(posY.toFixed(3)),
          cameraPositionZ: parseFloat(posZ.toFixed(3)),
          cameraTargetX: parseFloat(targetX.toFixed(3)),
          cameraTargetY: parseFloat(targetY.toFixed(3)),
          cameraTargetZ: parseFloat(targetZ.toFixed(3)),
          cameraFOV: parseFloat((cameraData.fov || 45).toFixed(1)),
        }))
      })
    } catch (error) {
      console.error("Failed to get camera position:", error)
      alert("Errore nel recupero della posizione camera dal visualizzatore")
    }
  }

  // Handle adding a new view
  const handleAddView = () => {
    setEditingView(null)
    setFormData({
      label: "",
      cameraPositionX: 0,
      cameraPositionY: 0,
      cameraPositionZ: 0,
      cameraTargetX: 0,
      cameraTargetY: 0,
      cameraTargetZ: 0,
      cameraFOV: 45,
      animate: false,
      animationDuration: 1.5,
      enabledForUsers: false,
      saveToFamily: false,
    })
    setCurrentViewData(null)
    setShowViewForm(true)
  }

  // Handle editing a view
  const handleEditView = (view, isFromFamily = false) => {
    setEditingView({ ...view, isFromFamily })
    setFormData({
      label: view.label || "",
      cameraPositionX: parseFloat(view.cameraPosition?.x || 0),
      cameraPositionY: parseFloat(view.cameraPosition?.y || 0),
      cameraPositionZ: parseFloat(view.cameraPosition?.z || 0),
      cameraTargetX: parseFloat(view.cameraTarget?.x || 0),
      cameraTargetY: parseFloat(view.cameraTarget?.y || 0),
      cameraTargetZ: parseFloat(view.cameraTarget?.z || 0),
      cameraFOV: parseFloat(view.cameraFOV || 45),
      animate: Boolean(view.animate),
      animationDuration: parseFloat(view.animationDuration || 1.5),
      enabledForUsers: Boolean(view.enabledForUsers),
      saveToFamily: isFromFamily,
    })
    setCurrentViewData(null)
    setShowViewForm(true)
  }

  // Handle deleting a view
  const handleDeleteView = async (viewLabel, isFromFamily = false) => {
    if (
      !window.confirm(`Sei sicuro di voler eliminare la view "${viewLabel}"?`)
    ) {
      return
    }

    setLoading(true)
    try {
      await deleteViewFromProduct(viewLabel, isFromFamily)
    } catch (error) {
      // Error already handled in delete functions
    } finally {
      setLoading(false)
    }
  }

  // Handle applying a view to the viewer
  const handleApplyView = (view) => {
    if (!window.emViewers || !window.emViewers["emersyaIframe"]) {
      alert("Visualizzatore 3D non disponibile")
      return
    }

    const viewer = window.emViewers["emersyaIframe"]

    try {
      // Ensure we have valid camera data
      const position = view.cameraPosition || { x: 0, y: 0, z: 0 }
      const target = view.cameraTarget || { x: 0, y: 0, z: 0 }
      const fov = view.cameraFOV || 45
      const animationDuration = view.animationDuration || 1.5

      viewer.setCamera({
        camera: {
          position: [position.x || 0, position.y || 0, position.z || 0],
          target: [target.x || 0, target.y || 0, target.z || 0],
          up: [0, 1, 0],
          transitionTime: view.animate ? animationDuration * 1000 : 500,
          fov: fov,
        },
      })

      console.log("Applied view:", view.label, { position, target, fov })
    } catch (error) {
      console.error("Failed to apply view:", error)
      alert("Errore nell'applicazione della view al visualizzatore")
    }
  }

  // Handle form input changes
  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target

    if (type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        [name]: checked,
      }))
    } else if (type === "number") {
      setFormData((prev) => ({
        ...prev,
        [name]: value === "" ? 0 : parseFloat(value),
      }))
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }))
    }
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validate form
    if (!formData.label.trim()) {
      alert("Il campo Label è obbligatorio")
      return
    }

    const targetViews = formData.saveToFamily ? familyViews : productViews

    // Check for duplicate labels when adding
    if (
      !editingView &&
      targetViews.some((view) => view.label === formData.label)
    ) {
      alert("Esiste già una view con questo nome")
      return
    }

    // Prepare view data
    const viewData = {
      label: formData.label,
      cameraPosition: {
        x: formData.cameraPositionX,
        y: formData.cameraPositionY,
        z: formData.cameraPositionZ,
      },
      cameraTarget: {
        x: formData.cameraTargetX,
        y: formData.cameraTargetY,
        z: formData.cameraTargetZ,
      },
      cameraFOV: formData.cameraFOV,
      animate: formData.animate,
      animationDuration: formData.animationDuration,
      enabledForUsers: formData.enabledForUsers,
    }

    console.log(
      "Submitting view data:",
      viewData,
      "saveToFamily:",
      formData.saveToFamily
    )

    setLoading(true)
    try {
      if (editingView) {
        // Update existing view
        const isFromFamily = editingView.isFromFamily || formData.saveToFamily
        await updateViewInProduct(editingView.label, viewData, isFromFamily)
      } else {
        // Add new view
        await saveViewToProduct(viewData, formData.saveToFamily)
      }

      setShowViewForm(false)
    } catch (error) {
      // Error already handled in save functions
    } finally {
      setLoading(false)
    }
  }

  if (!selectedProduct || !selectedProductCode) {
    return (
      <div className="ay-h-full ay-flex ay-flex-col">
        <div className="ay-flex-1 ay-flex ay-items-center ay-justify-center">
          <p className="ay-text-gray-500">
            Seleziona un prodotto per gestire le custom views
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="ay-h-full ay-flex ay-flex-col">
      {/* View Form Modal */}
      {showViewForm && (
        <div className="ay-fixed ay-inset-0 ay-bg-black ay-bg-opacity-50 ay-flex ay-items-center ay-justify-center ay-z-50">
          <div className="ay-bg-white ay-p-6 ay-rounded-lg ay-shadow-lg ay-w-full ay-max-w-2xl ay-max-h-screen ay-overflow-auto">
            <h2 className="ay-text-xl ay-font-bold ay-mb-4">
              {editingView ? "Modifica View" : "Aggiungi Nuova View"}
            </h2>

            <form onSubmit={handleSubmit}>
              <div className="ay-mb-4">
                <label className="ay-block ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-1">
                  Nome View *
                </label>
                <input
                  type="text"
                  name="label"
                  value={formData.label}
                  onChange={handleFormChange}
                  className="ay-block ay-w-full ay-px-3 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-shadow-sm ay-focus:ay-outline-none ay-focus:ay-ring-blue-500 ay-focus:ay-border-blue-500"
                  placeholder="es. Vista Frontale, Dettaglio, ecc."
                  required
                />
              </div>

              <div className="ay-mb-4 ay-flex ay-justify-between ay-items-center">
                <button
                  type="button"
                  onClick={getCurrentCameraPosition}
                  className="ay-px-4 ay-py-2 ay-bg-blue-500 ay-text-white ay-rounded ay-hover:ay-bg-blue-600"
                >
                  📷 Prendi Posizione Corrente
                </button>

                <div className="ay-flex ay-gap-4">
                  {/* Save to Family option */}
                  {familyData && (
                    <label className="ay-flex ay-items-center ay-gap-2">
                      <input
                        type="checkbox"
                        name="saveToFamily"
                        checked={formData.saveToFamily}
                        onChange={handleFormChange}
                        className="ay-h-4 ay-w-4 ay-text-blue-600 ay-rounded"
                      />
                      <span className="ay-text-sm">Salva per Famiglia</span>
                    </label>
                  )}

                  <label className="ay-flex ay-items-center ay-gap-2">
                    <input
                      type="checkbox"
                      name="enabledForUsers"
                      checked={formData.enabledForUsers}
                      onChange={handleFormChange}
                      className="ay-h-4 ay-w-4 ay-text-blue-600 ay-rounded"
                    />
                    <span className="ay-text-sm">Visibile agli Utenti</span>
                  </label>
                </div>
              </div>

              <div className="ay-grid ay-grid-cols-1 md:ay-grid-cols-3 ay-gap-4 ay-mb-4">
                <div>
                  <h3 className="ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-2">
                    Posizione Camera
                  </h3>
                  <div className="ay-space-y-2">
                    <div>
                      <label className="ay-block ay-text-xs ay-text-gray-500">
                        X
                      </label>
                      <input
                        type="number"
                        name="cameraPositionX"
                        value={formData.cameraPositionX}
                        onChange={handleFormChange}
                        step="0.001"
                        className="ay-block ay-w-full ay-px-3 ay-py-1 ay-border ay-border-gray-300 ay-rounded-md ay-text-sm"
                      />
                    </div>
                    <div>
                      <label className="ay-block ay-text-xs ay-text-gray-500">
                        Y
                      </label>
                      <input
                        type="number"
                        name="cameraPositionY"
                        value={formData.cameraPositionY}
                        onChange={handleFormChange}
                        step="0.001"
                        className="ay-block ay-w-full ay-px-3 ay-py-1 ay-border ay-border-gray-300 ay-rounded-md ay-text-sm"
                      />
                    </div>
                    <div>
                      <label className="ay-block ay-text-xs ay-text-gray-500">
                        Z
                      </label>
                      <input
                        type="number"
                        name="cameraPositionZ"
                        value={formData.cameraPositionZ}
                        onChange={handleFormChange}
                        step="0.001"
                        className="ay-block ay-w-full ay-px-3 ay-py-1 ay-border ay-border-gray-300 ay-rounded-md ay-text-sm"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-2">
                    Target Camera
                  </h3>
                  <div className="ay-space-y-2">
                    <div>
                      <label className="ay-block ay-text-xs ay-text-gray-500">
                        X
                      </label>
                      <input
                        type="number"
                        name="cameraTargetX"
                        value={formData.cameraTargetX}
                        onChange={handleFormChange}
                        step="0.001"
                        className="ay-block ay-w-full ay-px-3 ay-py-1 ay-border ay-border-gray-300 ay-rounded-md ay-text-sm"
                      />
                    </div>
                    <div>
                      <label className="ay-block ay-text-xs ay-text-gray-500">
                        Y
                      </label>
                      <input
                        type="number"
                        name="cameraTargetY"
                        value={formData.cameraTargetY}
                        onChange={handleFormChange}
                        step="0.001"
                        className="ay-block ay-w-full ay-px-3 ay-py-1 ay-border ay-border-gray-300 ay-rounded-md ay-text-sm"
                      />
                    </div>
                    <div>
                      <label className="ay-block ay-text-xs ay-text-gray-500">
                        Z
                      </label>
                      <input
                        type="number"
                        name="cameraTargetZ"
                        value={formData.cameraTargetZ}
                        onChange={handleFormChange}
                        step="0.001"
                        className="ay-block ay-w-full ay-px-3 ay-py-1 ay-border ay-border-gray-300 ay-rounded-md ay-text-sm"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="ay-text-sm ay-font-medium ay-text-gray-700 ay-mb-2">
                    Altre Impostazioni
                  </h3>
                  <div className="ay-space-y-2">
                    <div>
                      <label className="ay-block ay-text-xs ay-text-gray-500">
                        Campo Visivo (FOV)
                      </label>
                      <input
                        type="number"
                        name="cameraFOV"
                        value={formData.cameraFOV}
                        onChange={handleFormChange}
                        min="10"
                        max="120"
                        step="0.1"
                        className="ay-block ay-w-full ay-px-3 ay-py-1 ay-border ay-border-gray-300 ay-rounded-md ay-text-sm"
                      />
                    </div>

                    <div className="ay-mt-4">
                      <label className="ay-flex ay-items-center ay-gap-2 ay-cursor-pointer">
                        <input
                          type="checkbox"
                          name="animate"
                          checked={formData.animate}
                          onChange={handleFormChange}
                          className="ay-h-4 ay-w-4 ay-text-blue-600 ay-rounded"
                        />
                        <span className="ay-text-sm">Anima Transizione</span>
                      </label>
                    </div>

                    {formData.animate && (
                      <div>
                        <label className="ay-block ay-text-xs ay-text-gray-500">
                          Durata Animazione (sec)
                        </label>
                        <input
                          type="number"
                          name="animationDuration"
                          value={formData.animationDuration}
                          onChange={handleFormChange}
                          min="0.1"
                          max="10"
                          step="0.1"
                          className="ay-block ay-w-full ay-px-3 ay-py-1 ay-border ay-border-gray-300 ay-rounded-md ay-text-sm"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {currentViewData && (
                <div className="ay-bg-blue-50 ay-p-3 ay-rounded-md ay-mb-4">
                  <p className="ay-text-sm ay-text-blue-800">
                    ✅ Posizione camera corrente applicata al form.
                  </p>
                </div>
              )}

              <div className="ay-flex ay-justify-end ay-gap-3 ay-mt-6">
                <button
                  type="button"
                  onClick={() => setShowViewForm(false)}
                  className="ay-px-4 ay-py-2 ay-border ay-border-gray-300 ay-rounded-md ay-shadow-sm ay-bg-white ay-text-gray-700 ay-hover:ay-bg-gray-50"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  className="ay-px-4 ay-py-2 ay-border ay-border-transparent ay-rounded-md ay-shadow-sm ay-bg-blue-500 ay-text-white ay-hover:ay-bg-blue-600"
                >
                  {editingView ? "Aggiorna" : "Crea"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="ay-flex-1 ay-overflow-auto ay-p-4">
        {/* Header with actions */}
        <div className="ay-flex ay-justify-between ay-items-center ay-mb-4">
          <div>
            <h3 className="ay-text-lg ay-font-bold">
              {selectedProduct?.name || selectedProduct?.lang?.it}
            </h3>
            <p className="ay-text-sm ay-text-gray-600">
              Codice: {selectedProduct?.code} | Famiglia:{" "}
              {selectedProduct?.family}
            </p>
          </div>
          <button
            onClick={handleAddView}
            className="ay-px-3 ay-py-1 ay-bg-green-500 ay-text-white ay-rounded ay-hover:ay-bg-green-600 ay-text-sm"
          >
            ➕ Aggiungi View
          </button>
        </div>

        {/* Product Views */}
        <div className="ay-bg-white ay-rounded-lg ay-shadow ay-overflow-hidden ay-mb-4">
          <div className="ay-p-3 ay-border-b ay-bg-blue-50">
            <h4 className="ay-font-medium ay-text-blue-800">
              Views Prodotto Specifiche
            </h4>
            <p className="ay-text-xs ay-text-blue-600">
              Viste create specificamente per questo prodotto
            </p>
          </div>

          {productViews.length > 0 ? (
            <div className="ay-divide-y ay-divide-gray-200">
              {productViews.map((view, index) => (
                <div
                  key={index}
                  className="ay-p-3 ay-flex ay-justify-between ay-items-center"
                >
                  <div>
                    <h5 className="ay-font-medium ay-flex ay-items-center ay-gap-2">
                      {view.label}
                      {view.enabledForUsers && (
                        <span className="ay-text-xs ay-bg-green-100 ay-text-green-800 ay-px-2 ay-py-1 ay-rounded">
                          Visibile agli utenti
                        </span>
                      )}
                    </h5>
                    <div className="ay-text-xs ay-text-gray-500 ay-mt-1">
                      Pos: [{(view.cameraPosition?.x || 0).toFixed(2)},{" "}
                      {(view.cameraPosition?.y || 0).toFixed(2)},{" "}
                      {(view.cameraPosition?.z || 0).toFixed(2)}]
                      {view.animate &&
                        ` • Animata (${view.animationDuration || 1.5}s)`}
                    </div>
                  </div>
                  <div className="ay-flex ay-gap-1">
                    <button
                      onClick={() => handleApplyView(view)}
                      className="ay-px-2 ay-py-1 ay-bg-gray-200 ay-text-gray-800 ay-rounded ay-text-xs ay-hover:ay-bg-gray-300"
                    >
                      🎬
                    </button>
                    <button
                      onClick={() => handleEditView(view, false)}
                      className="ay-px-2 ay-py-1 ay-bg-blue-500 ay-text-white ay-rounded ay-text-xs ay-hover:ay-bg-blue-600"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleDeleteView(view.label, false)}
                      className="ay-px-2 ay-py-1 ay-bg-red-500 ay-text-white ay-rounded ay-text-xs ay-hover:ay-bg-red-600"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="ay-p-4 ay-text-center">
              <p className="ay-text-gray-500 ay-text-sm">
                Nessuna custom view per questo prodotto.
              </p>
            </div>
          )}
        </div>

        {/* Family Views */}
        {familyViews.length > 0 && (
          <div className="ay-bg-white ay-rounded-lg ay-shadow ay-overflow-hidden">
            <div className="ay-p-3 ay-border-b ay-bg-green-50">
              <h4 className="ay-font-medium ay-text-green-800">
                Views Famiglia
              </h4>
              <p className="ay-text-xs ay-text-green-600">
                Viste ereditate dalla famiglia "{selectedProduct?.family}"
              </p>
            </div>

            <div className="ay-divide-y ay-divide-gray-200">
              {familyViews.map((view, index) => (
                <div
                  key={index}
                  className="ay-p-3 ay-flex ay-justify-between ay-items-center"
                >
                  <div>
                    <h5 className="ay-font-medium ay-flex ay-items-center ay-gap-2">
                      {view.label}
                      <span className="ay-text-xs ay-bg-green-100 ay-text-green-800 ay-px-2 ay-py-1 ay-rounded">
                        Famiglia
                      </span>
                      {view.enabledForUsers && (
                        <span className="ay-text-xs ay-bg-blue-100 ay-text-blue-800 ay-px-2 ay-py-1 ay-rounded">
                          Visibile agli utenti
                        </span>
                      )}
                    </h5>
                    <div className="ay-text-xs ay-text-gray-500 ay-mt-1">
                      Pos: [{(view.cameraPosition?.x || 0).toFixed(2)},{" "}
                      {(view.cameraPosition?.y || 0).toFixed(2)},{" "}
                      {(view.cameraPosition?.z || 0).toFixed(2)}]
                      {view.animate &&
                        ` • Animata (${view.animationDuration || 1.5}s)`}
                    </div>
                  </div>
                  <div className="ay-flex ay-gap-1">
                    <button
                      onClick={() => handleApplyView(view)}
                      className="ay-px-2 ay-py-1 ay-bg-gray-200 ay-text-gray-800 ay-rounded ay-text-xs ay-hover:ay-bg-gray-300"
                    >
                      🎬
                    </button>
                    <button
                      onClick={() => handleEditView(view, true)}
                      className="ay-px-2 ay-py-1 ay-bg-blue-500 ay-text-white ay-rounded ay-text-xs ay-hover:ay-bg-blue-600"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleDeleteView(view.label, true)}
                      className="ay-px-2 ay-py-1 ay-bg-red-500 ay-text-white ay-rounded ay-text-xs ay-hover:ay-bg-red-600"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ViewsTab
